import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Crown, Trophy, Users, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const TYPE_LABEL = {
  volume: "Total Volume (kg)",
  workouts: "Workouts Completed",
  reps: "Reps",
  bodyweight_loss: "Body Weight Lost (kg)",
  cardio_distance: "Cardio Distance (km)",
  cardio_duration: "Cardio Time (min)",
};

export default function CommunityChallenges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/community-challenges?status=active");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const join = async (cid) => {
    setBusy(cid);
    try {
      await api.post(`/community-challenges/${cid}/join`);
      toast.success("You're in! Get to work 💪");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const leave = async (cid) => {
    setBusy(cid);
    try {
      await api.post(`/community-challenges/${cid}/leave`);
      toast.success("Left challenge");
      load();
    } finally { setBusy(null); }
  };
  const openDetail = async (cid) => {
    setOpen(cid);
    const { data } = await api.get(`/community-challenges/${cid}`);
    setDetail(data);
  };

  const active = items.filter((x) => x.status === "active");
  if (active.length === 0) return null;

  // Sort weekly missions to the top so they're the hero on the dashboard.
  const sortedActive = [...active].sort((a, b) => Number(!!b.is_weekly) - Number(!!a.is_weekly));

  const daysLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  return (
    <section data-testid="community-challenges" className="border border-blue-500/40 bg-gradient-to-br from-blue-500/5 to-transparent p-4 mb-2">
      <div className="flex items-center gap-2 mb-3">
        <Crown className="w-4 h-4 text-blue-500" />
        <h3 className="font-display text-lg uppercase">Live Community Challenges</h3>
      </div>
      <div className="space-y-2">
        {sortedActive.map((ch) => (
          <div key={ch.challenge_id} data-testid={`cc-${ch.challenge_id}`} className={`border ${ch.is_weekly ? "border-blue-500 bg-blue-500/10" : "border-neutral-800 bg-neutral-950"} p-3`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(ch.challenge_id)}>
                {ch.is_weekly && (
                  <div data-testid={`weekly-badge-${ch.challenge_id}`} className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-blue-500 font-bold mb-1">
                    <Calendar className="w-3 h-3" /> This Week's Mission
                  </div>
                )}
                <div className="font-display text-lg uppercase">{ch.title}</div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  {TYPE_LABEL[ch.challenge_type]} · {ch.duration_days}d · {ch.participant_count} joined
                  {ch.is_weekly && daysLeft(ch.end_date) != null && <span className="text-blue-400"> · {daysLeft(ch.end_date)}d left</span>}
                </div>
                {ch.description && <div className="text-xs text-neutral-300 mt-1">{ch.description}</div>}
              </div>
              {ch.joined ? (
                <Button data-testid={`leave-cc-${ch.challenge_id}`} size="sm" variant="outline" onClick={() => leave(ch.challenge_id)} disabled={busy === ch.challenge_id} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-8">In</Button>
              ) : (
                <Button data-testid={`join-cc-${ch.challenge_id}`} size="sm" onClick={() => join(ch.challenge_id)} disabled={busy === ch.challenge_id} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">
                  {busy === ch.challenge_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Join"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detail overlay — wrapped in a Radix Dialog so screen readers
          announce it as a modal, focus traps inside, and Esc closes it.
          The earlier hand-rolled div-on-overlay had none of these a11y
          guarantees. */}
      <Dialog
        open={!!(open && detail)}
        onOpenChange={(o) => {
          if (!o) { setOpen(null); setDetail(null); }
        }}
      >
        <DialogContent
          data-testid="cc-detail-dialog"
          className="bg-neutral-950 border-neutral-800 text-white max-w-md p-0 overflow-hidden"
        >
          {detail && (
            <>
              <DialogHeader className="p-5 border-b border-neutral-800 text-left">
                {detail.is_weekly && (
                  <div className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-blue-500 font-bold mb-2">
                    <Calendar className="w-3 h-3" /> This Week's Mission
                  </div>
                )}
                <DialogTitle className="font-display text-2xl uppercase leading-none">
                  {detail.title}
                </DialogTitle>
                <DialogDescription className="text-xs text-neutral-500 mt-1">
                  {TYPE_LABEL[detail.challenge_type]} · {detail.duration_days} days · ends {new Date(detail.end_date).toLocaleDateString()}
                </DialogDescription>
                {detail.description && (
                  <div className="text-sm text-neutral-300 mt-2">{detail.description}</div>
                )}
              </DialogHeader>
              <div className="p-5 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-4 h-4 text-blue-500" />
                  <h4 className="font-display text-lg uppercase">Leaderboard</h4>
                </div>
                {detail.leaderboard.length === 0 ? (
                  <div className="text-sm text-neutral-500 text-center py-6">
                    <Users className="w-6 h-6 text-neutral-600 mx-auto mb-2" /> No one's joined yet — be first.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {detail.leaderboard.map((row, i) => (
                      <div key={row.user_id} className={`flex items-center gap-3 p-2 ${row.user_id === user.user_id ? "bg-blue-500/10 border-l-2 border-blue-500" : ""}`}>
                        <div className="font-display text-2xl text-neutral-500 w-6">{i + 1}</div>
                        <div className="w-8 h-8 bg-blue-500 flex items-center justify-center font-display text-sm">{row.name?.[0]?.toUpperCase()}</div>
                        <div className="flex-1 truncate text-sm">{row.name}{row.user_id === user.user_id ? " (you)" : ""}</div>
                        <div className="font-mono-d text-sm text-blue-500">
                          {detail.challenge_type === "workouts" || detail.challenge_type === "reps" ? Math.round(row.score) : row.score.toFixed(1)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
